import { css } from "@emotion/react";
import { get } from "aws-amplify/api";
import Moment from "moment";
import React, { useState, useEffect, useMemo } from "react";
import { Accordion, Button, Col, Form, Row } from "react-bootstrap";
import "react-datepicker/dist/react-datepicker.css";
import Select from "react-select";
// First way to import
import { PacmanLoader } from "react-spinners";
import { createColumnHelper } from "@tanstack/react-table";
import { GenericTable } from "components/GenericTable";
import { createElementIdFactory, createReusableTableElementIds } from "utils/elementIds";
import "containers/common/List.css";
import "containers/common/Table.css";
import { PAGE_SIZES } from "utils/pageSizeConfig";
import { getProofingColumnMeta, proofingWrapMeta } from "utils/proofingTable";

const columnHelper = createColumnHelper();

function OtherProofing({isAuthenticated}) {
  const detailIdPrefix = "proofing-detail";

  const [isLoading, setIsLoading] = useState(null);
  const [cityscopeOptions, setCityscopeOptions] = useState([]);
  const [selectedCityscope, setSelectedCityscope] = useState(null);
  const [cityscope, setCityscope] = useState(null);
  const [scopeChecks, setScopeChecks] = useState(null);
  const [ledChecks, setLedChecks] = useState(null);


  // --- Data Fetching and Update Functions ---

  useEffect(() => {
    setIsLoading(true);
    (async () => {
      try {
        const localCityscopeOptions = await getCityscopes();
        if (localCityscopeOptions) {
          setCityscopeOptions(localCityscopeOptions);
          document.title = "Cityscope Proofing";
        }
      } catch (e) {
        alert(e);
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  async function getCityscopes() {
    const response = await get({
      apiName: "colin",
      path: "/cityscopes/dropdown",
    }).response;
    try {
      const cs = await response.body.json();
      return cs;
    } catch (error) {
      alert(error + ". Please refresh the page (F5) to try again.");
    }
  }

  async function getScopeChecks(filter) {
    const options = {
      queryParams: filter,
    };
    const response = await get({
      apiName: "colin",
      path: "/proofing/scopechecks",
      options,
    }).response;
    try {
      const cs = await response.body.json();
      return cs;
    } catch (error) {
      alert(error + ". Please refresh the page (F5) to try again.");
    }
  }

  async function getLedChecks(filter) {
    const options = {
      queryParams: filter,
    };
    const response = await get({
      apiName: "colin",
      path: "/proofing/ledchecks",
      options,
    }).response;
    try {
      const cs = await response.body.json();
      return cs;
    } catch (error) {
      alert(error + ". Please refresh the page (F5) to try again.");
    }
  }

  async function handleCityscopeChange(selectedCityscope) {
    setSelectedCityscope(selectedCityscope);
    setCityscope(
      selectedCityscope ? selectedCityscope.map((c) => c.value).join(",") : null
    );
  }

  const handleScopeChecks = async (e) => {
    if (selectedCityscope && selectedCityscope.length > 10) {
      alert("Please limit the number of cityscopes to 10.");
      return;
    }

    setIsLoading(true);
    try {
      setLedChecks(null);
      const localscopeChecks = await getScopeChecks({ scope: cityscope });
      if (localscopeChecks) {
        setScopeChecks(normalizeChecks(localscopeChecks, true));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleLedChecks = async (e) => {
    if (selectedCityscope && selectedCityscope.length > 10) {
      alert("Please limit the number of cityscopes to 10.");
      return;
    }

    setIsLoading(true);
    try {
      setScopeChecks(null);
      const localledchecks = await getLedChecks({ scope: cityscope });
      if (localledchecks) {
        setLedChecks(normalizeChecks(localledchecks));
      }
    } finally {
      setIsLoading(false);
    }
  };

  function titleCase(str) {
    if (str != null) {
      str = str.toLowerCase().split("_");
      let final = [];
      for (let word of str) {
        final.push(word.charAt(0).toUpperCase() + word.slice(1));
      }
      return final.join(" ");
    } else {
      return str;
    }
  }

  function isSelectedValue(value) {
    if (value === true || value === 1) return true;
    if (value === null || value === undefined || value === "") return true;
    if (typeof value === "string") {
      const normalized = value.trim().toLowerCase();
      return normalized === "true" || normalized === "1" || normalized === "y" || normalized === "yes";
    }
    return false;
  }

  function normalizeChecks(checks, defaultSelected = false) {
    return (checks || []).map((check) => ({
      ...check,
      proofing: {
        ...check.proofing,
        res: (check?.proofing?.res || []).map((record) => {
          const hasSelectedKey = Object.prototype.hasOwnProperty.call(record || {}, "selected");

          if (!hasSelectedKey) {
            return record;
          }

          return {
            ...record,
            selected: defaultSelected ? true : isSelectedValue(record.selected),
          };
        }),
      },
    }));
  }

  const renderProofingValue = (columnId, value) => {
    if (columnId.toLowerCase().indexOf("date") >= 0 && value !== null && value !== undefined && value !== "") {
      return Moment(new Date(value)).local().format("DD-MM-YYYY");
    }

    if (value === true || value === false) {
      return (
        <Form.Check
          checked={value}
          label=""
          disabled
          type="checkbox"
          style={{ textAlign: "center" }}
        />
      );
    }

    return value;
  };

  const handleCheckboxChange = (rowIndex, checkId, event) => {
    const isChecked = event.target.checked;

    if (scopeChecks) {
      setScopeChecks((prevChecks) =>
        (prevChecks || []).map((check) => {
          if (check?.proofing?.id !== checkId) {
            return check;
          }

          return {
            ...check,
            proofing: {
              ...check.proofing,
              res: (check.proofing?.res || []).map((record, index) =>
                index === rowIndex ? { ...record, selected: isChecked } : record
              ),
            },
          };
        })
      );
    }

    if (ledChecks) {
      setLedChecks((prevChecks) =>
        (prevChecks || []).map((check) => {
          if (check?.proofing?.id !== checkId) {
            return check;
          }

          return {
            ...check,
            proofing: {
              ...check.proofing,
              res: (check.proofing?.res || []).map((record, index) =>
                index === rowIndex ? { ...record, selected: isChecked } : record
              ),
            },
          };
        })
      );
    }
  };

  const renderEditable = (rowIndex, isChecked, checkId) => (
    <Form.Check
      checked={isSelectedValue(isChecked)}
      label=""
      onChange={(event) => handleCheckboxChange(rowIndex, checkId, event)}
      type="checkbox"
      style={{ textAlign: "center" }}
    />
  );

  const createCheckTableElementIds = (checkType, checkId) =>
    createReusableTableElementIds(
      createElementIdFactory(`${detailIdPrefix}-${checkType}-check-${checkId}`)
    );

  const createProofingColumns = (check, checkType) => {
    const rows = check?.proofing?.res || [];
    if (rows.length === 0) return [];

    const hasSelectedColumn = rows.some((row) =>
      Object.prototype.hasOwnProperty.call(row || {}, "selected")
    );

    const getTableBrowseListParam = (idKey) => {
      if (!hasSelectedColumn) {
        return "";
      }

      const selectableRows = (rows || []).filter((record) =>
        Object.prototype.hasOwnProperty.call(record || {}, "selected")
      );

      if (selectableRows.length <= 1) {
        return "";
      }

      return selectableRows
        .filter((record) => isSelectedValue(record.selected))
        .map((record) => record[idKey])
        .filter(Boolean)
        .join(",");
    };

    const propertyBrowseList = checkType === "scope" ? getTableBrowseListParam("property_key") : "";
    const leaseBrowseList = checkType === "led" ? getTableBrowseListParam("lease_id") : "";

    const columnKeys = Object.keys(rows[0]).filter((key) => key !== "selected");
    const orderedKeys = hasSelectedColumn ? ["selected", ...columnKeys] : columnKeys;

    return orderedKeys.map((key) => {
      if (key === "selected") {
        return columnHelper.accessor((row) => row.selected, {
          id: key,
          header: "",
          cell: (info) => renderEditable(info.row.index, info.getValue(), check.proofing.id),
          size: 60,
          enableSorting: false,
          meta: { align: "center" },
        });
      }

      if (key === "property_key") {
        return columnHelper.accessor((row) => row[key], {
          id: key,
          header: "Cityscope Ref",
          cell: (info) => {
            const value = info.getValue();
            if (checkType === "scope") {
              const href = propertyBrowseList
                ? `/properties/${value}?browselist=${propertyBrowseList}`
                : `/properties/${value}`;
              return <a href={href} target="new" rel="noopener noreferrer">{value}</a>;
            }

            return <a href={`/properties/${value}`} target="new" rel="noopener noreferrer">{value}</a>;
          },
          enableSorting: true,
        });
      }

      if (key === "entity_id") {
        return columnHelper.accessor((row) => row[key], {
          id: key,
          header: titleCase(key),
          cell: (info) => {
            const value = info.getValue();
            return <a href={`/entities/${value}`} target="new" rel="noopener noreferrer">{value}</a>;
          },
        });
      }

      if (key === "owner_id") {
        return columnHelper.accessor((row) => row[key], {
          id: key,
          header: titleCase(key),
          cell: (info) => {
            const value = info.getValue();
            return <a href={`/owners/${value}`} target="new" rel="noopener noreferrer">{value}</a>;
          },
        });
      }

      if (key === "sale_id") {
        return columnHelper.accessor((row) => row[key], {
          id: key,
          header: titleCase(key),
          cell: (info) => {
            const value = info.getValue();
            return <a href={`/sales/${value}`} target="new" rel="noopener noreferrer">{value}</a>;
          },
        });
      }

      if (key === "lease_id") {
        return columnHelper.accessor((row) => row[key], {
          id: key,
          header: titleCase(key),
          cell: (info) => {
            const value = info.getValue();
            const propertyKey = info.row.original.property_key;
            const href = checkType === "led" && leaseBrowseList
              ? `/properties/${propertyKey}/leases/${value}?browselist=${leaseBrowseList}`
              : `/properties/${propertyKey}/leases/${value}`;
            return <a href={href} target="new" rel="noopener noreferrer">{value}</a>;
          },
        });
      }

      if (key === "description") {
        return columnHelper.accessor((row) => row[key], {
          id: key,
          header: titleCase(key),
          size: 400,
          cell: (info) => (
            <span style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
              {renderProofingValue(key, info.getValue())}
            </span>
          ),
          meta: proofingWrapMeta,
        });
      }

      return columnHelper.accessor((row) => row[key], {
        id: key,
        header: titleCase(key),
        cell: (info) => renderProofingValue(key, info.getValue()),
        meta: getProofingColumnMeta(key),
      });
    }).filter(Boolean);
  };

  const createId = useMemo(
    () => createElementIdFactory(detailIdPrefix),
    [detailIdPrefix]
  );


  // --- Render Functions ---

  function render() {
    return (
      <div id={createId("proofing")} className="Proofing">
        {isLoading && (
          <div id={createId("loader", "outer")} className="outer-div">
            <div id={createId("loader", "inner")} className="sweet-loading inner-div">
              <PacmanLoader
                css={css`
                  display: block;
                  margin: 0 auto;
                  border-color: red;
                `}
                size={20}
                color={"#e03c31"}
                loading={isLoading}
              />
            </div>
          </div>
        )}

        {cityscopeOptions && !isLoading && (
          <div id={createId("form", "container")} className="ProofingForm">
            <Row id={createId("title", "row")}>
              <Col id={createId("title", "col")} sm={12} md={12} lg={12}>
                <h6 id={createId("title")}>Cityscope Proofing</h6>
              </Col>
            </Row>
            <Form id={createId("filter", "form")}>
              <Row
                id={createId("filter", "row")}
                className="justify-content-md-center"
                style={{ alignItems: "flex-start", marginBottom: "16px" }}
              >
                <Col id={createId("cityscope", "col")} sm={2} md={2} lg={2}>
                  <Form.Group controlId="cityscope">
                    <Select
                      value={selectedCityscope}
                      onChange={handleCityscopeChange}
                      options={cityscopeOptions}
                      placeholder="Cityscope"
                      isMulti
                      inputId={createId("cityscope", "input")}
                      instanceId={createId("cityscope", "instance")}
                    />
                  </Form.Group>
                </Col>

                <Col
                  id={createId("checks", "col")}
                  sm="auto"
                  style={{ display: "flex", gap: "8px", marginBottom: "12px", alignItems: "flex-start" }}
                >
                  <Button
                    id={createId("scope", "checks", "button")}
                    variant="primary"
                    onClick={handleScopeChecks}
                    style={{ height: "38px", minHeight: "38px", whiteSpace: "nowrap" }}
                  >
                    Property Checks
                  </Button>
                  <Button
                    id={createId("led", "checks", "button")}
                    variant="primary"
                    onClick={handleLedChecks}
                    style={{ height: "38px", minHeight: "38px", whiteSpace: "nowrap" }}
                  >
                    Tenant/Lease Checks
                  </Button>
                </Col>
              </Row>
            </Form>

            <div id={createId("results")} className="proofResults">
              {scopeChecks && (
                <div id={createId("scope", "results")}>
                  <Accordion id={createId("scope", "accordion")} className="proofing-accordion">
                    {scopeChecks.map((check, index) => {
                      const checkId = check?.proofing?.id ?? index;
                      const eventKey = `scope-${index}`;

                      return (
                        <Accordion.Item
                          id={`${detailIdPrefix}-scope-item-${checkId}`}
                          key={eventKey}
                          eventKey={eventKey}
                        >
                          <Accordion.Header id={`${detailIdPrefix}-scope-accordion-header-${checkId}`}>
                            <span id={`${detailIdPrefix}-scope-title-${checkId}`} className="proofing-accordion-title">
                              {check.proofing.query_name}: Fail results:{" "}
                              {check.proofing.res ? (
                                <span className="proofing-fail-count">
                                  {check.proofing.res.length}
                                </span>
                              ) : (
                                0
                              )}
                            </span>
                          </Accordion.Header>
                          <Accordion.Body id={`${detailIdPrefix}-scope-accordion-body-${checkId}`} className="px-0">
                            <div id={`${detailIdPrefix}-scope-card-body-${checkId}`} className="px-0">
                              <p id={`${detailIdPrefix}-scope-description-${checkId}`} className="px-3">{check.proofing.query_desc}</p>
                              {check.proofing.res && check.proofing.res.length > 0 && (
                                <div id={`${detailIdPrefix}-scope-table-wrapper-${checkId}`} className="w-100">
                                  <GenericTable
                                    columns={createProofingColumns(check, "scope")}
                                    data={check.proofing.res}
                                    section={`proofing-scope-${checkId}`}
                                    elementIds={createCheckTableElementIds("scope", checkId)}
                                    pageSize={PAGE_SIZES.otherProofingPanel}
                                    enablePagination={true}
                                    getRowId={(row, rowIndex) => `${checkId}-${rowIndex}`}
                                  />
                                </div>
                              )}
                            </div>
                          </Accordion.Body>
                        </Accordion.Item>
                      );
                    })}
                  </Accordion>
                </div>
              )}

              {ledChecks && (
                <div id={createId("led", "results")}>
                  <Accordion id={createId("led", "accordion")} className="proofing-accordion">
                    {ledChecks.map((check, index) => {
                      const checkId = check?.proofing?.id ?? index;
                      const eventKey = `led-${index}`;

                      return (
                        <Accordion.Item
                          id={`${detailIdPrefix}-led-item-${checkId}`}
                          key={eventKey}
                          eventKey={eventKey}
                        >
                          <Accordion.Header id={`${detailIdPrefix}-led-accordion-header-${checkId}`}>
                            <span id={`${detailIdPrefix}-led-title-${checkId}`} className="proofing-accordion-title">
                              {check.proofing.query_name}: Fail results:{" "}
                              {check.proofing.res ? (
                                <span className="proofing-fail-count">
                                  {check.proofing.res.length}
                                </span>
                              ) : (
                                0
                              )}
                            </span>
                          </Accordion.Header>
                          <Accordion.Body id={`${detailIdPrefix}-led-accordion-body-${checkId}`} className="px-0">
                            <div id={`${detailIdPrefix}-led-card-body-${checkId}`} className="px-0">
                              <p id={`${detailIdPrefix}-led-description-${checkId}`} className="px-3">{check.proofing.query_desc}</p>
                              {check.proofing.res && check.proofing.res.length > 0 && (
                                <div id={`${detailIdPrefix}-led-table-wrapper-${checkId}`} className="w-100">
                                  <GenericTable
                                    columns={createProofingColumns(check, "led")}
                                    data={check.proofing.res}
                                    section={`proofing-led-${checkId}`}
                                    elementIds={createCheckTableElementIds("led", checkId)}
                                    pageSize={PAGE_SIZES.otherProofingPanel}
                                    enablePagination={true}
                                    getRowId={(row, rowIndex) => `${checkId}-${rowIndex}`}
                                  />
                                </div>
                              )}
                            </div>
                          </Accordion.Body>
                        </Accordion.Item>
                      );
                    })}
                  </Accordion>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div id={createId("container")} className="ProofingDetail">
      {isAuthenticated ? render() : null}
    </div>
  );
}
export default OtherProofing;
